/**
 * Dashboard v2 section + attention widgets. Reuses S42 block renderers so
 * figures match chat (Gate 5).
 */
import { el } from '../../core/dom.js';
import { empty } from '../../components/empty.js';
import { button } from '../../components/button.js';
import { withProduct } from '../../core/route-map.js';
import { renderBlocks } from '../../components/blocks/index.js';
import { figureText } from '../../components/blocks/kpi-figure.js';
import { widgetMeta, SECTION_IDS } from './layout.js';

function cardShell(title, body) {
  return el('article', { class: 'dash-w dash-section', 'aria-label': title },
    el('div', { class: 'dash-wbody' }, body));
}

/**
 * @param {object} block resolved kpi block from top_row
 * @param {string} productId
 */
export function topKpiFromBlock(block, productId) {
  const item = block && block.resolved && block.resolved.items && block.resolved.items[0];
  const title = (item && item.label) || widgetMeta('mau').title;
  if (!item || item.value == null || item.text === '—') {
    return cardShell(title, empty({
      title: `Connect a source to see ${title}`,
      body: 'Synced analytics fill this tile.',
      action: button('Open connectors', { href: withProduct('/app/connectors', productId) }),
    }));
  }
  const deltaObj = item.delta && typeof item.delta === 'object' ? item.delta : null;
  const deltaText = deltaObj ? deltaObj.text : item.delta;
  const tone = deltaObj && deltaObj.tone ? deltaObj.tone : 'neutral';
  const arrow = tone === 'bad' ? '↓' : '↑';
  const fresh = block.source && block.source.freshness;
  const stale = block.source && block.source.stale;
  return cardShell(title, el('div', { class: 'dash-kpi' },
    el('div', { class: 'dash-kicker dash-rule' },
      el('span', {}, block.code || 'KPI'),
      el('span', {}, block.window || 'Last 30 days')),
    el('div', { class: 'dash-kicker is-name' }, title),
    el('div', { class: 'dash-kpi-value' },
      el('span', { class: 'dash-figure' }, figureText(item)),
      deltaText
        ? el('span', { class: `dash-delta is-${tone}` }, `${arrow} ${deltaText} vs prior 30 days`)
        : null),
    item.caption ? el('p', { class: 'dash-note' }, item.caption) : null,
    fresh ? el('p', { class: stale ? 'dash-note is-stale' : 'dash-note' },
      stale ? `${fresh} · stale` : fresh) : null));
}

/**
 * @param {object} block depth block from top_row
 * @param {string} productId
 * @param {Function} depthCard existing depth card renderer
 */
export function topDepthFromBlock(block, productId, depthCard) {
  const resolved = (block && block.resolved) || { analysis_depth: 0, depth_breakdown: {} };
  return depthCard(resolved, productId);
}

/**
 * @param {string} id section widget id
 * @param {object} v2 dashboard v2 payload
 * @param {string} productId
 */
export function sectionCard(id, v2, productId) {
  const meta = widgetMeta(id);
  const section = ((v2 && v2.sections) || []).find((s) => s.id === id);
  if (!section || !section.blocks || !section.blocks.length) {
    const hidden = ((v2 && v2.hidden) || []).find((h) => h.id === id);
    return cardShell(meta.title, empty({
      title: (hidden && hidden.label) || `Connect a source to see ${meta.title}`,
      body: 'This section stays hidden until a source can fill it.',
      action: button('Open connectors', { href: withProduct('/app/connectors', productId) }),
    }));
  }
  return cardShell(meta.title, el('div', { class: 'dash-stack' },
    el('div', { class: 'dash-kicker dash-rule' },
      el('span', {}, section.title || meta.title),
      el('span', {}, `${section.blocks.length} tiles`)),
    renderBlocks(section.blocks)));
}

/**
 * @param {object} v2
 * @param {string} productId
 */
export function attentionCard(v2, productId) {
  const items = (v2 && v2.attention) || [];
  if (!items.length) {
    return cardShell('Needs attention', empty({
      title: 'Nothing needs attention',
      body: 'Failed schedules, broken connectors and off-track targets show up here.',
    }));
  }
  return cardShell('Needs attention', el('div', { class: 'dash-stack' },
    el('div', { class: 'dash-kicker dash-rule' },
      el('span', {}, 'Needs attention'),
      el('span', {}, `${items.length} items`)),
    el('ul', { class: 'dash-list' }, items.map((item, index) => el('li', {},
      el('span', { class: 'dash-index' }, String(index + 1).padStart(2, '0')),
      el('div', { class: 'dash-list-main' },
        el('span', { class: 'dash-list-title' }, item.title || item.kind),
        item.detail ? el('span', { class: 'dash-note' }, item.detail) : null),
      actionButton(item, productId))))));
}

function actionButton(item, productId) {
  const action = item.action || {};
  if (action.type === 'ask') {
    const q = encodeURIComponent(action.prompt || item.title || '');
    return button(action.label || 'Ask Bonnie', {
      href: withProduct(`/app/chat/new?q=${q}`, productId),
    });
  }
  const href = action.href
    ? withProduct(action.href, productId)
    : withProduct('/app/connectors', productId);
  return button(action.label || 'Open', { href });
}

/**
 * One line listing hidden sections (D3).
 * @param {object} v2
 * @param {string} productId
 */
export function hiddenLine(v2, productId) {
  const hidden = (v2 && v2.hidden) || [];
  if (!hidden.length) return null;
  return el('p', { class: 'dash-hidden' },
    el('span', { class: 'dash-kicker' }, 'Not shown yet'),
    ' ',
    hidden.map((h) => h.label).join(' · '),
    ' ',
    button('Connectors', { href: withProduct('/app/connectors', productId) }));
}

export function isSectionId(id) {
  return SECTION_IDS.includes(id);
}
